import { type RefObject, useLayoutEffect, useState } from 'react';

type ElementSize = Readonly<{
    width: number;
    height: number;
}>;

const INITIAL_SIZE: ElementSize = {
    width: 0,
    height: 0,
};

export const useSizeObserver = <T extends HTMLElement>(
    elementRef: RefObject<T | null>,
    refreshDependency?: unknown,
): ElementSize => {
    const [size, setSize] = useState<ElementSize>(INITIAL_SIZE);

    const refreshKey = JSON.stringify(refreshDependency);

    useLayoutEffect(() => {
        const element = elementRef?.current;

        if (!element) {
            return;
        }

        const updateSize = (): void => {
            const width = element.clientWidth;
            const height = element.clientHeight;

            setSize((currentSize) => {
                if (
                    currentSize.width === width &&
                    currentSize.height === height
                ) {
                    return currentSize;
                }

                return { width, height };
            });
        };

        updateSize();

        const resizeObserver = new ResizeObserver(updateSize);
        resizeObserver.observe(element);

        return (): void => resizeObserver.disconnect();
    }, [elementRef, refreshKey]);

    return size;
};
